import { ExpandMoreIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import PlaceTypeIcon from '@components/PlaceTypeIcon'
import { usePortalDropdownPosition } from '@hooks'
import { newSearchSession, searchPlaces, SearchSession } from '@lib/googlePlaces'
import { GooglePlace, PlaceDraft, placePicked } from '@lib/place'
import {
	PLACE_SEARCH_DEBOUNCE_MS,
	PlaceSearchContext,
	StartRow,
	placeOfRow,
	placeSearchStart,
	searchesGoogle,
} from '@lib/placeSearch'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
	AutocompleteContainer,
	AutocompleteDropdown,
	ClearButton,
	CustomLocationItem,
	EmptyAutocompleteItem,
	IconSlot,
	Input,
	MainText,
	PlaceItem,
	RowText,
	SecondaryText,
	SectionHeader,
	ShowAllItem,
} from './LocationAutocomplete.styles'
import { TestIds } from '@/testIds'

interface LocationAutocompleteProps {
	value: string
	onChange: (value: string) => void
	/** A place picked from the list: a Search result, a Trip place or a Suggestion. */
	onPlaceSelect: (place: PlaceDraft) => void
	placeholder?: string
	id?: string
	autoFocus?: boolean
	/** Accessible name when no visible `<label>` points at the input. */
	ariaLabel?: string
	/**
	 * Opts the field into free text. Given it, the list always ends with a
	 * "use it as typed" row carrying the trimmed query, and Enter picks that
	 * row — so a place Google doesn't know is reachable whenever something is
	 * typed, not only when the search comes back empty.
	 */
	onFreeTextSelect?: (text: string) => void
	/**
	 * The trip the search is for: the Trip places and Suggestions it opens on
	 * before anything is typed, and the destination typed searches lean toward.
	 */
	context?: PlaceSearchContext
}

/** What a place's row shows: its name, its address when there is one, and the types its icon is drawn from. */
interface PlaceRow {
	main: string
	secondary: string | null
	types: readonly string[] | null | undefined
}

const startRowPlace = (row: Exclude<StartRow, { kind: 'showAllTripPlaces' }>): PlaceRow =>
	row.kind === 'tripPlace'
		? {
				main: row.location.primaryText || row.location.fullText,
				secondary: row.location.secondaryText || null,
				types: row.location.googleTypes,
			}
		: { main: row.suggestion.name, secondary: row.suggestion.address, types: row.suggestion.types }

/** A place in the list, drawn as iOS draws it: the icon its types call for, then its name over its address. */
const PlaceRowContent: React.FC<PlaceRow> = ({ main, secondary, types }) => (
	<>
		<IconSlot>
			<PlaceTypeIcon types={types} sx={{ fontSize: iconSizes.xl }} />
		</IconSlot>
		<RowText>
			<MainText>{main}</MainText>
			{secondary && <SecondaryText>{secondary}</SecondaryText>}
		</RowText>
	</>
)

const startRowKey = (row: StartRow): string =>
	row.kind === 'tripPlace'
		? `trip:${row.location.googlePlaceId ?? row.location.fullText}`
		: row.kind === 'suggestion'
			? `suggestion:${row.suggestion.placeId}`
			: 'showAll'

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
	value,
	onChange,
	onPlaceSelect,
	placeholder = 'Search for a location...',
	id,
	autoFocus = false,
	ariaLabel,
	onFreeTextSelect,
	context,
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const [results, setResults] = useState<GooglePlace[]>([])
	const [isLoading, setIsLoading] = useState(false)
	/** Once asked, every Trip place shows for as long as the field is up. */
	const [showsAllTripPlaces, setShowsAllTripPlaces] = useState(false)
	/** The billing session the searches toward the next pick share; started on the first of them. */
	const sessionRef = useRef<SearchSession | null>(null)
	/** Read when a search goes out, so it leans toward the destination the search is for by then. */
	const biasRef = useRef(context?.scope?.bias ?? null)
	biasRef.current = context?.scope?.bias ?? null
	const containerRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const dropdownRef = useRef<HTMLDivElement>(null)
	const dropdownCoords = usePortalDropdownPosition(containerRef, isOpen)

	const query = value.trim()
	/** A single character is too vague to search for, or to take as a place. */
	const searches = searchesGoogle(query)
	/** Until there is enough to search for, the list is what the search opens on. */
	const startSections = !searches && context ? placeSearchStart({ ...context, showsAllTripPlaces }) : []
	const shownResults = searches ? results : []
	const allowFreeText = onFreeTextSelect !== undefined
	/** The trailing "use it as typed" row is offered whenever there is enough to search for. */
	const showCustomRow = allowFreeText && searches
	const isSearching = isLoading && shownResults.length === 0 && searches
	/** Google came back with nothing — said in the list, above the custom row. */
	const showNoMatchesNote = showCustomRow && !isLoading && shownResults.length === 0
	const hasRows = startSections.length > 0 || shownResults.length > 0 || showCustomRow || isSearching

	// Search once typing pauses. An answer to text that has since changed is dropped.
	useEffect(() => {
		if (!searches) {
			setResults([])
			setIsLoading(false)
			return
		}
		if (!isOpen) return

		setIsLoading(true)
		let stale = false
		const timer = setTimeout(async () => {
			try {
				sessionRef.current ??= await newSearchSession()
				const found = await searchPlaces(query, sessionRef.current, biasRef.current)
				if (!stale) setResults(found)
			} catch {
				// Offline, no key, or Google refused: nothing found, and the custom row still stands.
				if (!stale) setResults([])
			} finally {
				if (!stale) setIsLoading(false)
			}
		}, PLACE_SEARCH_DEBOUNCE_MS)
		return () => {
			stale = true
			clearTimeout(timer)
		}
	}, [query, searches, isOpen])

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const insideContainer = containerRef.current?.contains(event.target as Node)
			const insideDropdown = dropdownRef.current?.contains(event.target as Node)
			if (!insideContainer && !insideDropdown) {
				setIsOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange(e.target.value)
		setIsOpen(true)
	}

	// A pick ends the billing session either way, so every path starts the next one.
	const startNewSession = () => {
		sessionRef.current = null
	}

	const handlePick = (place: PlaceDraft) => {
		setIsOpen(false)
		setResults([])
		onChange(place.text)
		onPlaceSelect(place)
		startNewSession()
	}

	const handleStartRowClick = (row: StartRow) => {
		if (row.kind === 'showAllTripPlaces') {
			setShowsAllTripPlaces(true)
			return
		}
		const place = placeOfRow(row)
		if (place) handlePick(place)
	}

	/**
	 * Takes the query as typed. The text is handed over untouched — clearing it
	 * or keeping it in the field is the caller's call.
	 */
	const handleCustomLocationClick = () => {
		if (!onFreeTextSelect || !showCustomRow) return
		setIsOpen(false)
		setResults([])
		onFreeTextSelect(query)
		startNewSession()
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key !== 'Enter' || !allowFreeText) return
		e.preventDefault()
		handleCustomLocationClick()
	}

	const handleBlur = () => {
		setIsOpen(false)
	}

	const handleClear = () => {
		onChange('')
		setResults([])
		// Cleared, the field is back to what the search opens on, if anything.
		setIsOpen(true)
		inputRef.current?.focus()
	}

	// Short of a search, focus opens on what the search starts with — shown as
	// soon as there is any, so places still loading appear when they land.
	const handleFocus = () => {
		if (!searches || shownResults.length > 0 || showCustomRow) {
			setIsOpen(true)
		}
	}

	return (
		<AutocompleteContainer ref={containerRef}>
			<Input
				ref={inputRef}
				id={id}
				aria-label={ariaLabel}
				data-testid={TestIds.location.searchField}
				type="text"
				value={value}
				onChange={handleInputChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				placeholder={placeholder}
				autoComplete="off"
				autoFocus={autoFocus}
			/>
			{value && (
				<ClearButton type="button" onClick={handleClear} aria-label="Clear location">
					×
				</ClearButton>
			)}
			{isOpen &&
				dropdownCoords &&
				hasRows &&
				createPortal(
					<AutocompleteDropdown
						ref={dropdownRef}
						$top={dropdownCoords.top}
						$left={dropdownCoords.left}
						$width={dropdownCoords.width}
					>
						{startSections.flatMap(section => [
							<SectionHeader key={`section:${section.title}`}>{section.title}</SectionHeader>,
							...section.rows.map(row => {
								const onMouseDown = (e: React.MouseEvent) => {
									e.preventDefault() // Prevent input blur
									handleStartRowClick(row)
								}
								if (row.kind === 'showAllTripPlaces') {
									return (
										<ShowAllItem key={startRowKey(row)} onMouseDown={onMouseDown}>
											<IconSlot $muted aria-hidden>
												<ExpandMoreIcon sx={{ fontSize: iconSizes.sm }} />
											</IconSlot>
											Show all {row.count}
										</ShowAllItem>
									)
								}
								return (
									<PlaceItem key={startRowKey(row)} onMouseDown={onMouseDown}>
										<PlaceRowContent {...startRowPlace(row)} />
									</PlaceItem>
								)
							}),
						])}
						{isSearching && <EmptyAutocompleteItem>Searching...</EmptyAutocompleteItem>}
						{showNoMatchesNote && (
							<EmptyAutocompleteItem>No locations found for “{query}”</EmptyAutocompleteItem>
						)}
						{shownResults.map((result) => (
							<PlaceItem
								key={result.placeId}
								onMouseDown={(e) => {
									e.preventDefault() // Prevent input blur
									handlePick(placePicked(result))
								}}
							>
								<PlaceRowContent
									main={result.primaryText}
									secondary={result.secondaryText || null}
									types={result.types}
								/>
							</PlaceItem>
						))}
						{showCustomRow && (
							<CustomLocationItem
								data-testid={TestIds.location.useCustomButton}
								onMouseDown={(e) => {
									e.preventDefault() // Prevent input blur
									handleCustomLocationClick()
								}}
							>
								<IconSlot>
									<PlaceTypeIcon types={[]} sx={{ fontSize: iconSizes.xl }} />
								</IconSlot>
								Use “{query}” as a custom location
							</CustomLocationItem>
						)}
					</AutocompleteDropdown>,
					document.body,
				)}
		</AutocompleteContainer>
	)
}

export default LocationAutocomplete
